import { useState } from "react";
import PageHeader from "@/components/PageHeader";
import { useJobLog, type JobLogEntry } from "@/hooks/use-job-log";
import { useHaptics } from "@/hooks/use-haptics";
import { Trash2, ChevronDown, ChevronUp, AlertTriangle, Copy, FileText, Share2, Download } from "lucide-react";
import { generateJobLogPDF } from "@/components/job-log/generate-pdf";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const formatEntryText = (entry: JobLogEntry) => {
  const date = new Date(entry.timestamp);
  const dateStr = date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  const timeStr = date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  const lines = [
    `${typeLabels[entry.calcType]} — ${entry.label}`,
    `Date: ${dateStr} ${timeStr}`,
    entry.note ? `Note: ${entry.note}` : "",
    "",
    "Inputs:",
    ...Object.entries(entry.inputs).map(([k, v]) => `  ${k}: ${v}`),
    "",
    "Results:",
    ...entry.results.map((r) => `  ${r.label}: ${r.value} ${r.unit}`),
  ];
  return lines.filter((l) => l !== "").join("\n");
};

const formatAllEntriesText = (entries: JobLogEntry[]) => {
  return [
    "═══ Job Log Export ═══",
    `${entries.length} calculation${entries.length !== 1 ? "s" : ""}`,
    `Exported: ${new Date().toLocaleString()}`,
    "",
    ...entries.map((e, i) => `── ${i + 1} ──\n${formatEntryText(e)}`),
  ].join("\n\n");
};

const typeLabels: Record<string, string> = {
  "voltage-drop": "⚡ Voltage Drop",
  "pipe-velocity": "🔧 Pipe Velocity",
  "btu-sizing": "🔥 BTU Sizing",
  "conduit-fill": "🔌 Conduit Fill",
  "conduit-bending": "📐 Conduit Bending",
  "ampacity-derate": "🌡️ Ampacity Derate",
  "box-fill": "📦 Box Fill",
  "transformer-kva": "🔄 Transformer kVA",
  "motor-starter": "⚙️ Motor Starter",
  "motor-feeder": "⚙️ Motor Feeder",
  "wire-sizing": "🔌 Wire Sizing",
  "egc-lookup": "🟢 EGC Lookup",
  "service-entrance": "🏠 Service Entrance",
  "panel-schedule": "📊 Panel Schedule",
  "demand-factor": "📉 Demand Factor",
  "fault-current": "⚡ Fault Current",
  "vfd-sizing": "🔄 VFD Sizing",
  "disconnect-sizing": "🔲 Disconnect Sizing",
  "power-factor-correction": "📐 Power Factor",
};

const typeBadgeColors: Record<string, string> = {
  "voltage-drop": "bg-primary/20 text-primary",
  "pipe-velocity": "bg-[hsl(var(--pipe-green))]/20 text-[hsl(var(--pipe-green))]",
  "btu-sizing": "bg-destructive/20 text-destructive",
  "conduit-fill": "bg-accent/20 text-accent-foreground",
  "conduit-bending": "bg-secondary text-secondary-foreground",
  "ampacity-derate": "bg-copper/20 text-copper",
  "box-fill": "bg-secondary text-secondary-foreground",
  "transformer-kva": "bg-primary/20 text-primary",
  "motor-starter": "bg-accent/20 text-accent-foreground",
  "motor-feeder": "bg-accent/20 text-accent-foreground",
  "wire-sizing": "bg-primary/20 text-primary",
  "egc-lookup": "bg-[hsl(var(--pipe-green))]/20 text-[hsl(var(--pipe-green))]",
  "service-entrance": "bg-copper/20 text-copper",
  "panel-schedule": "bg-secondary text-secondary-foreground",
  "demand-factor": "bg-secondary text-secondary-foreground",
  "fault-current": "bg-destructive/20 text-destructive",
  "vfd-sizing": "bg-primary/20 text-primary",
  "disconnect-sizing": "bg-accent/20 text-accent-foreground",
  "power-factor-correction": "bg-secondary text-secondary-foreground",
};

const EntryCard = ({ entry, onDelete }: { entry: JobLogEntry; onDelete: () => void }) => {
  const [expanded, setExpanded] = useState(false);
  const { lightTap, warning } = useHaptics();
  const date = new Date(entry.timestamp);
  const dateStr = date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  const timeStr = date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });

  return (
    <div className="trade-card">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${typeBadgeColors[entry.calcType]}`}>
              {typeLabels[entry.calcType]}
            </span>
            <span className="text-[10px] text-muted-foreground">{dateStr} {timeStr}</span>
          </div>
          <p className="text-sm font-semibold text-foreground truncate">{entry.label}</p>
          {entry.note && <p className="text-xs text-muted-foreground mt-0.5 truncate">📝 {entry.note}</p>}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={() => {
              lightTap();
              const text = formatEntryText(entry);
              if (navigator.share) {
                navigator.share({ title: entry.label, text }).catch(() => {});
              } else {
                navigator.clipboard.writeText(text);
                toast.success("Copied to clipboard");
              }
            }}
            className="p-1.5 text-muted-foreground hover:text-foreground transition-colors"
          >
            <Share2 className="w-4 h-4" />
          </button>
          <button onClick={() => { lightTap(); setExpanded(!expanded); }} className="p-1.5 text-muted-foreground hover:text-foreground transition-colors">
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          <button onClick={() => { warning(); onDelete(); }} className="p-1.5 text-muted-foreground hover:text-destructive transition-colors">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Key result preview */}
      {!expanded && entry.results.length > 0 && (
        <div className="flex gap-3 mt-2">
          {entry.results.slice(0, 2).map((r, i) => (
            <div key={i} className="text-xs">
              <span className="text-muted-foreground">{r.label}: </span>
              <span className="font-mono-data text-primary font-bold">{r.value}</span>
              <span className="text-muted-foreground"> {r.unit}</span>
            </div>
          ))}
        </div>
      )}

      {/* Expanded details */}
      {expanded && (
        <div className="mt-3 space-y-2 border-t border-border pt-3">
          <div>
            <h4 className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wider mb-1">Inputs</h4>
            <div className="grid grid-cols-2 gap-1">
              {Object.entries(entry.inputs).map(([k, v]) => (
                <div key={k} className="text-xs">
                  <span className="text-muted-foreground">{k}: </span>
                  <span className="font-mono-data text-foreground">{v}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h4 className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wider mb-1">Results</h4>
            {entry.results.map((r, i) => (
              <div key={i} className="flex justify-between items-center py-1">
                <span className="text-xs text-muted-foreground">{r.label}</span>
                <span className="font-mono-data text-sm text-primary font-bold">
                  {r.value} <span className="text-xs text-muted-foreground font-normal">{r.unit}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const JobLog = () => {
  const { entries, deleteEntry, clearAll } = useJobLog();

  return (
    <div className="pb-24 animate-fade-in">
      <PageHeader title="📋 Job Log" subtitle="Saved calculator results" />
      <div className="px-4 mt-3 space-y-3">
        {entries.length > 0 && (
          <div className="flex justify-between items-center">
            <span className="text-xs text-muted-foreground">{entries.length} saved calculation{entries.length !== 1 ? "s" : ""}</span>
            <div className="flex items-center gap-1">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="text-xs text-muted-foreground hover:text-foreground">
                    <Share2 className="w-3 h-3 mr-1" /> Export
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => {
                    navigator.clipboard.writeText(formatAllEntriesText(entries));
                    toast.success("Copied all entries to clipboard");
                  }}>
                    <Copy className="w-4 h-4 mr-2" /> Copy as Text
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => {
                    const w = window.open("", "_blank");
                    if (!w) return;
                    w.document.write(`<html><head><title>Job Log</title><style>body{font-family:system-ui,sans-serif;padding:2rem;max-width:700px;margin:auto}h1{font-size:1.4rem}h2{font-size:1rem;margin-top:1.5rem;border-bottom:1px solid #ccc;padding-bottom:4px}.meta{color:#666;font-size:.85rem}.row{display:flex;justify-content:space-between;padding:2px 0;font-size:.9rem}.label{color:#666}</style></head><body><h1>📋 Job Log</h1><p class="meta">${entries.length} calculation${entries.length !== 1 ? "s" : ""} · Exported ${new Date().toLocaleString()}</p>`);
                    entries.forEach((e, i) => {
                      const d = new Date(e.timestamp);
                      w.document.write(`<h2>${i + 1}. ${typeLabels[e.calcType]} — ${e.label}</h2>`);
                      w.document.write(`<p class="meta">${d.toLocaleDateString()} ${d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}${e.note ? ` · 📝 ${e.note}` : ""}</p>`);
                      w.document.write(`<p><strong>Inputs</strong></p>`);
                      Object.entries(e.inputs).forEach(([k, v]) => w.document.write(`<div class="row"><span class="label">${k}</span><span>${v}</span></div>`));
                      w.document.write(`<p><strong>Results</strong></p>`);
                      e.results.forEach((r) => w.document.write(`<div class="row"><span class="label">${r.label}</span><span><b>${r.value}</b> ${r.unit}</span></div>`));
                    });
                    w.document.write(`</body></html>`);
                    w.document.close();
                    w.print();
                  }}>
                    <FileText className="w-4 h-4 mr-2" /> Print / Save PDF
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => {
                    generateJobLogPDF(entries);
                    toast.success("PDF downloaded");
                  }}>
                    <Download className="w-4 h-4 mr-2" /> Download PDF
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="ghost" size="sm" className="text-xs text-destructive hover:text-destructive">
                    <Trash2 className="w-3 h-3 mr-1" /> Clear All
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle className="flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-destructive" /> Clear all entries?
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      This will permanently delete all {entries.length} saved calculations. This cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={clearAll} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                      Delete All
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        )}

        {entries.length === 0 && (
          <div className="text-center py-16">
            <p className="text-4xl mb-3">📋</p>
            <p className="text-muted-foreground text-sm">No saved calculations yet</p>
            <p className="text-muted-foreground text-xs mt-1">Run a calculator and tap "Save to Job Log" to keep a record</p>
          </div>
        )}

        {entries.map((entry) => (
          <EntryCard key={entry.id} entry={entry} onDelete={() => deleteEntry(entry.id)} />
        ))}
      </div>
    </div>
  );
};

export default JobLog;
